/**
 * Read-only schedule overview (week summary).
 * Does not run planDailySchedule — CONF-20260719-schedule-overview.
 */

import { supabase } from '../config/supabase';
import { draftStorageKey, parseDailyScheduleDraft } from '../scheduling/scheduleDraft';
import { addLocalDays, toLocalDateString } from '../utils/complianceReadiness';

export interface ScheduleDaySummary {
  date: string;
  unscheduledJobCount: number;
  publishedTripCount: number;
  hasLocalDraft: boolean;
  /** 0–1 heat vs active fleet size (capped). */
  occupancyRatio: number;
}

export interface ScheduleOverviewRange {
  fromDate: string;
  toDate: string;
  days: ScheduleDaySummary[];
  activeVehicleCount: number;
  totals: {
    unscheduledJobs: number;
    publishedTrips: number;
    draftDays: number;
  };
}

/** Monday of the ISO week containing `ymd` (local calendar). */
export function startOfIsoWeek(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  return addLocalDays(ymd, diff);
}

export function endOfIsoWeek(ymd: string): string {
  return addLocalDays(startOfIsoWeek(ymd), 6);
}

export function eachDateInclusive(fromDate: string, toDate: string): string[] {
  if (fromDate > toDate) return [];
  const out: string[] = [];
  let cur = fromDate;
  while (cur <= toDate) {
    out.push(cur);
    cur = addLocalDays(cur, 1);
  }
  return out;
}

export function hasLocalScheduleDraft(
  userId: string | null | undefined,
  scheduleDate: string
): boolean {
  if (!userId) return false;
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(draftStorageKey(userId, scheduleDate));
    if (!raw) return false;
    return parseDailyScheduleDraft(raw) != null;
  } catch {
    return false;
  }
}

export interface OverviewTripBlock {
  id: string;
  vehicleId: string;
  fleetNumber: string;
  pickupTime: string;
  endTime: string;
  label: string;
  passengerCount: number;
  status: string;
}

export interface OverviewUnscheduledJob {
  id: string;
  title: string;
  passengerCount: number;
  jobType: string;
  earliestStart: string | null;
}

export interface OverviewVehicleRow {
  id: string;
  fleetNumber: string;
}

export interface ScheduleOverviewDayDetail {
  date: string;
  vehicles: OverviewVehicleRow[];
  trips: OverviewTripBlock[];
  unscheduledJobs: OverviewUnscheduledJob[];
}

export async function fetchScheduleOverviewRange(params: {
  fromDate: string;
  toDate: string;
  /** When set, only count trips for these vehicles (Owner fleet filter). */
  vehicleIds?: string[] | null;
  userId?: string | null;
  /**
   * HQ unscheduled/planning jobs. Owner MVP: false (trips+fleet occupancy only).
   * @default true when vehicleIds unset; false when vehicleIds set
   */
  includeUnscheduledJobs?: boolean;
}): Promise<ScheduleOverviewRange> {
  const { fromDate, toDate, vehicleIds, userId } = params;
  const includeJobs =
    params.includeUnscheduledJobs ?? vehicleIds == null;
  const dates = eachDateInclusive(fromDate, toDate);

  // Empty fleet filter: do not query with a fake UUID (Postgres rejects non-UUID).
  if (vehicleIds && vehicleIds.length === 0) {
    const days: ScheduleDaySummary[] = dates.map((date) => ({
      date,
      unscheduledJobCount: 0,
      publishedTripCount: 0,
      hasLocalDraft: hasLocalScheduleDraft(userId, date),
      occupancyRatio: 0,
    }));
    return {
      fromDate,
      toDate,
      days,
      activeVehicleCount: 0,
      totals: {
        unscheduledJobs: 0,
        publishedTrips: 0,
        draftDays: days.filter((d) => d.hasLocalDraft).length,
      },
    };
  }

  let tripsQuery = supabase
    .from('trips')
    .select('id, date, vehicle_id')
    .gte('date', fromDate)
    .lte('date', toDate)
    .in('status', ['scheduled', 'dispatched', 'in_progress'])
    .not('vehicle_id', 'is', null);

  if (vehicleIds && vehicleIds.length > 0) {
    tripsQuery = tripsQuery.in('vehicle_id', vehicleIds);
  }

  let vehiclesQuery = supabase.from('vehicles').select('id').eq('status', 'active');
  if (vehicleIds && vehicleIds.length > 0) {
    vehiclesQuery = vehiclesQuery.in('id', vehicleIds);
  }

  const jobsPromise = includeJobs
    ? supabase
        .from('jobs')
        .select('id, schedule_date')
        .gte('schedule_date', fromDate)
        .lte('schedule_date', toDate)
        .in('status', ['unscheduled', 'planning'])
        .neq('assist_status', 'pending_platform')
    : Promise.resolve({ data: [] as { id: string; schedule_date: string }[], error: null });

  const [jobsRes, tripsRes, vehiclesRes] = await Promise.all([
    jobsPromise,
    tripsQuery,
    vehiclesQuery,
  ]);

  if (jobsRes.error) throw new Error(jobsRes.error.message);
  if (tripsRes.error) throw new Error(tripsRes.error.message);
  if (vehiclesRes.error) throw new Error(vehiclesRes.error.message);

  const jobCounts = new Map<string, number>();
  for (const row of jobsRes.data ?? []) {
    const d = row.schedule_date as string;
    jobCounts.set(d, (jobCounts.get(d) ?? 0) + 1);
  }

  const tripCounts = new Map<string, number>();
  for (const row of tripsRes.data ?? []) {
    const d = row.date as string;
    tripCounts.set(d, (tripCounts.get(d) ?? 0) + 1);
  }

  const activeVehicleCount = (vehiclesRes.data ?? []).length;
  const denom = Math.max(activeVehicleCount, 1);

  const days: ScheduleDaySummary[] = dates.map((date) => {
    const unscheduledJobCount = jobCounts.get(date) ?? 0;
    const publishedTripCount = tripCounts.get(date) ?? 0;
    const hasLocalDraft = hasLocalScheduleDraft(userId, date);
    return {
      date,
      unscheduledJobCount,
      publishedTripCount,
      hasLocalDraft,
      occupancyRatio: Math.min(1, publishedTripCount / denom),
    };
  });

  return {
    fromDate,
    toDate,
    days,
    activeVehicleCount,
    totals: {
      unscheduledJobs: days.reduce((s, d) => s + d.unscheduledJobCount, 0),
      publishedTrips: days.reduce((s, d) => s + d.publishedTripCount, 0),
      draftDays: days.filter((d) => d.hasLocalDraft).length,
    },
  };
}

/** Convenience: ISO week (Mon–Sun) containing today or a given date. */
export async function fetchScheduleOverviewIsoWeek(params: {
  anchorDate?: string;
  vehicleIds?: string[] | null;
  userId?: string | null;
  includeUnscheduledJobs?: boolean;
}): Promise<ScheduleOverviewRange> {
  const anchor = params.anchorDate ?? toLocalDateString();
  const fromDate = startOfIsoWeek(anchor);
  const toDate = endOfIsoWeek(anchor);
  return fetchScheduleOverviewRange({
    fromDate,
    toDate,
    vehicleIds: params.vehicleIds,
    userId: params.userId,
    includeUnscheduledJobs: params.includeUnscheduledJobs,
  });
}

function shortLabel(pickup: string, dropoff: string): string {
  const p = pickup.trim().slice(0, 28);
  const d = dropoff.trim().slice(0, 28);
  if (!p && !d) return 'Trip';
  return `${p || '…'} → ${d || '…'}`;
}

/** Selected-day read-only detail: published trip blocks + optional unscheduled jobs. */
export async function fetchScheduleOverviewDayDetail(params: {
  date: string;
  vehicleIds?: string[] | null;
  includeUnscheduledJobs?: boolean;
}): Promise<ScheduleOverviewDayDetail> {
  const { date, vehicleIds } = params;
  const includeJobs =
    params.includeUnscheduledJobs ?? vehicleIds == null;

  // Empty fleet filter: short-circuit (avoid invalid UUID sentinel "__none__").
  if (vehicleIds && vehicleIds.length === 0) {
    return { date, vehicles: [], trips: [], unscheduledJobs: [] };
  }

  let vehiclesQuery = supabase
    .from('vehicles')
    .select('id, fleet_number')
    .eq('status', 'active')
    .order('fleet_number');
  if (vehicleIds && vehicleIds.length > 0) {
    vehiclesQuery = vehiclesQuery.in('id', vehicleIds);
  }

  let tripsQuery = supabase
    .from('trips')
    .select(
      'id, vehicle_id, pickup_time, end_time, pickup_address, dropoff_address, passenger_count, status, vehicles(fleet_number)'
    )
    .eq('date', date)
    .in('status', ['scheduled', 'dispatched', 'in_progress'])
    .not('vehicle_id', 'is', null)
    .order('pickup_time');

  if (vehicleIds && vehicleIds.length > 0) {
    tripsQuery = tripsQuery.in('vehicle_id', vehicleIds);
  }

  const jobsPromise = includeJobs
    ? supabase
        .from('jobs')
        .select('id, title, passenger_count, job_type, job_legs(earliest_start, sequence)')
        .eq('schedule_date', date)
        .in('status', ['unscheduled', 'planning'])
        .neq('assist_status', 'pending_platform')
        .order('title')
    : Promise.resolve({ data: [] as unknown[], error: null });

  const [vehiclesRes, tripsRes, jobsRes] = await Promise.all([
    vehiclesQuery,
    tripsQuery,
    jobsPromise,
  ]);

  if (vehiclesRes.error) throw new Error(vehiclesRes.error.message);
  if (tripsRes.error) throw new Error(tripsRes.error.message);
  if (jobsRes.error) throw new Error(jobsRes.error.message);

  const vehicles: OverviewVehicleRow[] = (vehiclesRes.data ?? []).map((v) => ({
    id: v.id as string,
    fleetNumber: (v.fleet_number as string) || '—',
  }));

  const fleetById = new Map(vehicles.map((v) => [v.id, v.fleetNumber]));

  const trips: OverviewTripBlock[] = (tripsRes.data ?? []).map((row) => {
    const vehicleId = row.vehicle_id as string;
    const nested = row.vehicles as { fleet_number?: string } | { fleet_number?: string }[] | null;
    const nestedFleet = Array.isArray(nested)
      ? nested[0]?.fleet_number
      : nested?.fleet_number;
    return {
      id: row.id as string,
      vehicleId,
      fleetNumber: fleetById.get(vehicleId) || nestedFleet || '—',
      pickupTime: String(row.pickup_time ?? '').slice(0, 8),
      endTime: String(row.end_time ?? '').slice(0, 8),
      label: shortLabel(
        String(row.pickup_address ?? ''),
        String(row.dropoff_address ?? '')
      ),
      passengerCount: Number(row.passenger_count) || 0,
      status: String(row.status ?? ''),
    };
  });

  // Include vehicles that have trips but were filtered out of active list (edge)
  for (const t of trips) {
    if (!fleetById.has(t.vehicleId)) {
      vehicles.push({ id: t.vehicleId, fleetNumber: t.fleetNumber });
      fleetById.set(t.vehicleId, t.fleetNumber);
    }
  }

  const unscheduledJobs: OverviewUnscheduledJob[] = (jobsRes.data ?? []).map((row) => {
    const r = row as {
      id: string;
      title: string;
      passenger_count: number;
      job_type: string;
      job_legs?: { earliest_start: string; sequence: number }[] | null;
    };
    const legs = [...(r.job_legs ?? [])].sort((a, b) => a.sequence - b.sequence);
    return {
      id: r.id,
      title: r.title,
      passengerCount: r.passenger_count,
      jobType: r.job_type,
      earliestStart: legs[0]?.earliest_start?.slice(0, 5) ?? null,
    };
  });

  return { date, vehicles, trips, unscheduledJobs };
}
